import React, { useState, useEffect } from 'react';
import { Box, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import Papa from 'papaparse';
import { useSelector } from 'react-redux';
import { selectSession } from '@core/store/modules/session/sessionSelectors';
import { isRoleValid } from '@core/utils/roles/validateRoles';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import {
	SmartToolbarContainerFull,
	SmartToolbarGroupLeft,
	SmartToolbarGroupCenter,
	SmartToolbarGroupRight,
	SmartSwitch, SmartDivider, SmartTaskCount
} from '~/FielderElements/SmartTaskTable/SmartStyles';
import {
	MRT_ShowHideColumnsButton,
	MRT_ToggleFiltersButton,
	MRT_ToggleFullScreenButton
} from 'material-react-table';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PersonAdd, RemoveCircle as RemoveCircleIcon, UploadFile } from '@mui/icons-material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SmartUserTableFilter from '~/FielderElements/SmartUserTable/SmartUserTableFilter';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';

const SmartUserTableToolbar = (
	{ lan, permissionName, permissionTypeImport, permissionTypeExport, permissionTypeCreate,
		permissionTypeDisable, showImportUsers, doShowCreateUser, changeUsersShowing, showLocationToUpdate,
		showDisabledUsers, showCVS, exportableData, exportableDataHeaders, csvFileName, addText,
		numberText, textEnabledUser, textDisalbledUser, totalNumberOfUsers, filterProps, showCheckboxComponent,
		auxToolbarActions, table
	}) => {
	const session = useSelector(selectSession);
	const [allowed, setAllowed] = useState({
		import: false, export: false, create: false, disable: false,
	});
	const titleWithToolBar = `${numberText} ${showDisabledUsers ? textDisalbledUser : textEnabledUser}`
	const titleWithoutToolBar = `${textEnabledUser}`;
	const hasAuxToolbarActions = auxToolbarActions && Object.keys(auxToolbarActions).length > 0;
	useEffect(() => {
		const roles = session?.roles;
		setAllowed({
			import: isRoleValid(permissionName, permissionTypeImport, roles),
			export: isRoleValid(permissionName, permissionTypeExport, roles),
			create: isRoleValid(permissionName, permissionTypeCreate, roles),
			disable: isRoleValid(permissionName, permissionTypeDisable, roles),
		});
	}, [session, permissionName, permissionTypeImport, permissionTypeExport, permissionTypeCreate, permissionTypeDisable]);
	const handleExport = () => {
		if (!exportableData || !exportableDataHeaders) return;
		const csv = Papa.unparse({
			fields: exportableDataHeaders.map((h) => h.label),
			data: exportableData.map((d) => exportableDataHeaders.map((h) => d[h.key])),
		});
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', csvFileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};
	return (
		<Box sx={SmartToolbarContainerFull}>
			<Box sx={SmartToolbarGroupLeft}>
				{allowed.create && hasAuxToolbarActions && (
					<Tooltip arrow title={addText || 'Add'}>
						<IconButton
							onClick={doShowCreateUser}
							size='small'
							sx={{ color: '#dc5c87' }}
							disabled={!!showDisabledUsers}
						>
							<PersonAdd />
						</IconButton>
					</Tooltip>
				)}
				{allowed.import && hasAuxToolbarActions && (
					<Tooltip arrow title={lan.import}>
						<IconButton size="small" disabled={!!showDisabledUsers} onClick={showImportUsers}>
							<UploadFile />
						</IconButton>
					</Tooltip>
				)}
				{RoleUtils.isUserSuperAndSuperViewerAndAdministrator(session.user)
					&& hasAuxToolbarActions && allowed.export && showCVS && (
					<Tooltip arrow title={lan.export}>
						<IconButton size="small" onClick={handleExport}>
							<SaveAltIcon />
						</IconButton>
					</Tooltip>
				)}
			</Box>
			<Box sx={SmartToolbarGroupCenter}>
				{typeof showLocationToUpdate === 'function' && !showDisabledUsers && hasAuxToolbarActions && (
					<Tooltip arrow title={lan.showBranchesToUpdateLocation}>
						<FormControlLabel
							control={
								<SmartSwitch
									id='id-switch-branches' defaultValue={false}
									onChange={(e) => showLocationToUpdate(e.target.checked)} />}
							label={<NotListedLocationIcon fontSize={"medium"} sx={{ color: '#dc5c87' }}/>}
							labelPlacement={"start"}
							sx={{ marginTop: '9px', }}
						>
						</FormControlLabel>
					</Tooltip>
				)}
				{hasAuxToolbarActions &&
					<Tooltip arrow title={lan.showOnlyDeleted}>
						<FormControlLabel
							control={
								<SmartSwitch
									id='id-switch-agent'
									defaultValue={false}
									onChange={(e) => changeUsersShowing(e.target.checked)} />}
							label={<RemoveCircleIcon fontSize={"medium"} sx={{ color: '#dc5c87' }}/>}
							labelPlacement={"start"}
							sx={{ marginTop: '9px', }}
						>
						</FormControlLabel>
					</Tooltip>
				}
				<MRT_ToggleFiltersButton table={table} />
				{filterProps && (
					<SmartUserTableFilter {...filterProps} />
				)}
			</Box>
			<Box sx={SmartToolbarGroupRight}>
				{allowed.disable && showCheckboxComponent && hasAuxToolbarActions && (auxToolbarActions)}
				<MRT_ShowHideColumnsButton table={table} />
				<MRT_ToggleFullScreenButton table={table} />
				<SmartDivider orientation="vertical" variant="middle" flexItem />
				<Tooltip arrow title={`${hasAuxToolbarActions ? titleWithToolBar : titleWithoutToolBar}`}>
					<Button
						size="large"
						startIcon={showDisabledUsers ?
							<RemoveCircleIcon fontSize={"large"}/> :
							<CheckCircleIcon fontSize={"large"} />}
						loading={false}
						loadingPosition="start"
						disabled={false}
						style={{color: '#dc5c87'}}>
						<SmartTaskCount variant={'h5'}>{totalNumberOfUsers}</SmartTaskCount>
					</Button>
				</Tooltip>
			</Box>
		</Box>
	);
};
export default SmartUserTableToolbar;
